type UiLocale = 'zh-Hans' | 'en';

type SubjectModuleCopyCode = 'math' | 'yuwen' | 'english';

type SubjectModuleCopy = {
  title: string;
  sectionTitle: string;
  sectionHint: string;
  beta: string;
  required: string;
  expand: string;
  collapse: string;
  mathHint: string;
  optionalHint: string;
  toggleA11y: (subject: string) => string;
  modules: Record<SubjectModuleCopyCode, {
    name: string;
    tagline: string;
    body: string;
    features: [string, string, string, string];
  }>;
};

const ZH: SubjectModuleCopy = {
  title: '学科模块',
  sectionTitle: '加载学科引擎',
  sectionHint: '点按模块查看功能介绍。数学始终加载；语文与英语仍为 Beta，关闭后不会出现在首页学科菜单中。',
  beta: 'BETA',
  required: '必选',
  expand: '查看介绍',
  collapse: '收起介绍',
  mathHint: '基础模块，始终加载，不可关闭。',
  optionalHint: '关闭后，首页学科下拉菜单将不再显示该学科。',
  toggleA11y: (subject) => `加载或卸载${subject}模块`,
  modules: {
    math: {
      name: '数学',
      tagline: '基础学习引擎',
      body: '数学是颗粒日记最完整的学科引擎。从素材到知识颗粒，从每日打卡到主干学情，再到实战答题纸与品题，构成一条可闭环的学习路径。',
      features: [
        '每日打卡与间隔复习',
        '主干学情与学习覆盖度',
        '实战答题纸扫描与刻意练习',
        '品题：一题多解、归纳与浓缩',
      ],
    },
    yuwen: {
      name: '语文',
      tagline: '阅读与文言引擎',
      body: '语文模块仍在 Beta。加载后会出现在首页学科菜单中，把课文、阅读与文言材料沉淀为可复习的知识颗粒。',
      features: [
        '每日打卡与推荐序列',
        '文言文专项',
        '阅读理解专项',
        '实战答题纸局部练习（Beta）',
      ],
    },
    english: {
      name: '英语',
      tagline: '词汇与阅读引擎',
      body: '英语模块仍在 Beta。加载后会出现在首页学科菜单中，用打卡、词汇与阅读把素材变成可检索、可复习的颗粒。',
      features: [
        '每日打卡与推荐序列',
        '词汇专项',
        '阅读理解',
        '实战答题纸局部练习（Beta）',
      ],
    },
  },
};

const EN: SubjectModuleCopy = {
  title: 'Subject modules',
  sectionTitle: 'Load subject engines',
  sectionHint: 'Tap a module for its feature intro. Math stays loaded; Chinese and English are still Beta and drop off the home subject menu when unloaded.',
  beta: 'BETA',
  required: 'Required',
  expand: 'See features',
  collapse: 'Hide intro',
  mathHint: 'Base engine. Always loaded and cannot be turned off.',
  optionalHint: 'When unloaded, it disappears from the home subject menu.',
  toggleA11y: (subject) => `Load or unload the ${subject} module`,
  modules: {
    math: {
      name: 'Math',
      tagline: 'Core learning engine',
      body: 'Math is Keliriji’s most complete subject engine — from source materials to particles, daily drill to spine progress, then practice sheets and TTQ.',
      features: [
        'Daily drill and spaced review',
        'Spine progress and coverage',
        'Practice-sheet scan and deliberate practice',
        'TTQ: multiple solutions, patterns, and distillation',
      ],
    },
    yuwen: {
      name: 'Chinese',
      tagline: 'Reading & classical engine',
      body: 'Chinese is still in Beta. Load it to add Chinese to the home subject menu and turn passages, reading, and classical texts into reviewable particles.',
      features: [
        'Daily drill and recommended sequences',
        'Classical Chinese focus',
        'Reading comprehension focus',
        'Practice-sheet local drills (Beta)',
      ],
    },
    english: {
      name: 'English',
      tagline: 'Vocabulary & reading engine',
      body: 'English is still in Beta. Load it to add English to the home subject menu and turn materials into searchable, reviewable particles via drill, vocabulary, and reading.',
      features: [
        'Daily drill and recommended sequences',
        'Vocabulary focus',
        'Reading comprehension',
        'Practice-sheet local drills (Beta)',
      ],
    },
  },
};

function resolveUiLocale(): UiLocale {
  if (typeof navigator === 'undefined') {
    return 'zh-Hans';
  }
  return String(navigator.language || '').toLowerCase().startsWith('en') ? 'en' : 'zh-Hans';
}

export function getSubjectModulesCopy(locale: UiLocale = resolveUiLocale()): SubjectModuleCopy {
  return locale === 'en' ? EN : ZH;
}
